import { useMemo } from 'react'
import { useFetchDisciplinesQuery } from '../../../../store/apis/discipline.api'
import { useFetchConceptualLabelsForVersionQuery, useFetchConceptualLabelsQuery } from '../../../../store/apis/conceptual-label.api'

/**
 * Custom hook that combines discipline data with conceptual labels
 * Ensures all disciplines are shown, even those without labels
 * @param {Object} options - Query options
 * @param {boolean} options.includeInactive - Whether to include inactive disciplines
 * @param {boolean} options.includeSystem - Whether to include system disciplines
 * @returns {Object} Combined data and loading states
 */
const useConceptualLabelsByDiscipline = (options = {
    includeInactive: false,
    includeSystem: true,
    organizationOnly: false,
    includeEmpty: false,
    token: null
}) => {
    // Fetch all disciplines, including inactive ones if specified
    const { 
        data: disciplines, 
        isFetching: isFetchingDisciplines, 
        error: disciplinesError,
        refetch: refetchDisciplines
    } = useFetchDisciplinesQuery({ 
        includeInactive: options.includeInactive, 
        includeSystem: options.includeSystem 
    })

    const fetchConceptualLabels = options.token ? useFetchConceptualLabelsForVersionQuery : useFetchConceptualLabelsQuery
    
    // Fetch all conceptual labels
    const { 
        data: conceptualLabels, 
        isFetching: isFetchingLabels, 
        error: labelsError,
        refetch: refetchLabels
    } = fetchConceptualLabels({
        token: options.token,
        organizationOnly: options.organizationOnly,
    })

    // Assign child labels under their parent within a discipline
    const assignChildrenToParents = (labelMap, disciplineId) => {
        // Find all labels for this discipline
        const disciplineLabels = Object.values(labelMap).filter(label => 
            label.disciplineId === disciplineId
        )
        
        // Assign children to parents
        disciplineLabels.forEach(label => {
            if (label.parentId) {
                const parent = disciplineLabels.find(p => p.id === label.parentId)
                if (parent) {
                    parent.children = parent.children || []
                    // Only add if not already added
                    if (!parent.children.some(child => child.id === label.id)) {
                        parent.children.push(label)
                    }
                }
            }
        })
        
        // Return only root labels (those without parents in this discipline)
        return disciplineLabels.filter(label => !label.parentId)
    }

    // Combine the data
    const combinedData = useMemo(() => {
        if (!disciplines || !conceptualLabels) {
            return []
        }

        // Create a map of all conceptual labels by ID
        const labelMap = conceptualLabels.reduce((map, label) => {
            map[label.id] = { ...label }
            return map
        }, {})

        // Build the hierarchy
        const combinedData = disciplines.map(discipline => {
            // Get root labels for this discipline
            const rootLabels = assignChildrenToParents(labelMap, discipline.id)
            
            return {
                id: discipline.id,
                name: discipline.name,
                visualId: discipline.visualId,
                isActive: discipline.isActive,
                isDiscipline: true,
                children: rootLabels
            }
        })

        if (!options.includeEmpty) {
            return combinedData.filter(discipline => discipline.children.length > 0)
        }

        return combinedData
    }, [disciplines, conceptualLabels])

    // Combine refetch functions
    const refetch = () => {
        refetchDisciplines()
        refetchLabels()
    }

    return {
        data: combinedData,
        isFetching: isFetchingDisciplines || isFetchingLabels,
        error: disciplinesError || labelsError,
        refetch
    }
}

export default useConceptualLabelsByDiscipline 